import Image from "@/atoms-react/image/Image.react";
import { Base } from "@/atoms-react/text/Text.react";
import { LanguageSelector } from "@/components/lang-select/index.react";
import { useAuth } from "@/features/auth/hooks/use-auth-react";
import { getRoutesWithoutPadding as getRoutesWithoutSpacing } from "@/features/flow/constants";
import type React from "react";
import { useLocation, useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { AnimatedForm } from "./AnimatedForm.react";
import { OlkyIntroduction } from "./OlkyIntroduction.react";
import { FromURL } from "@/features/flow/types";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { usePartnerInfo } from "@/features/partner/hooks/use-partner-information";
import { convertToDesign, convertToDesignCards } from "@/features/partner/utils";
import { AttributesHtmlElement, typeElement } from "@/features/partner/types";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { useMemo } from "react";

type LayoutProps = React.ComponentPropsWithoutRef<"div"> &
  React.PropsWithChildren<{
    overlayMode?: boolean;
    whiteLabel?: boolean;
  }>;

export const Layout = ({ children, whiteLabel = false }: LayoutProps) => {
  const { cached } = useAuth();
  const { session_uuid, block } = useParams();
  const location = useLocation();
  const routesWithoutSpacing = getRoutesWithoutSpacing(session_uuid ?? "");
  const isRouteWithoutSpacing = routesWithoutSpacing.includes(location.pathname + location.search);

  const partner = (() => {
    const originUrl = document.location.origin;
    const clientURl = new URL(originUrl);
    const subdomain = clientURl.hostname.split(".")[0];
    if (!["pass", "newpass", "app"].includes(subdomain)) {
      return subdomain;
    }
    return sessionStorage.getItem("origin");
  })();

  const { partnerLoginDataQuery } = usePartnerInfo();
  const { data } = partnerLoginDataQuery(partner, block === "signup" ? "register" : "login");
  const newUser = !cached?.email && block === "welcome";
  const { getLanguage } = usei18n();

  whiteLabel = whiteLabel || !!new URLSearchParams(location.search).get("whitelabel");
  const overlayMode = whiteLabel;
  const origin = useOrigin();

  const getImageURL = (() => {
    if (FromURL.None === origin) return "/images/olkypass/olky-space-background.jpg";
    const bgImage = convertToDesignCards(data?.cards?.[0], typeElement.BACKGROUND);
    return getLanguage() === "fr" ? bgImage?.url_fr : bgImage?.url_en;
  })();

  const getLogo = (() => {
    if (FromURL.None === origin) return "/images/olkypass/olky-space-background.jpg";
    const logo = convertToDesignCards(data?.cards?.[0], typeElement.LOGO);
    return getLanguage() === "fr" ? logo?.url_fr : logo?.url_en;
  })();
  const getFooter = useMemo(() => {
    const text = convertToDesign(data, typeElement.FOOTER);
    return getLanguage() === "fr" ? text?.content_fr : text?.content_en;
  }, [origin, data]);
  const getAttribute = useMemo(() => {
    return data?.cards?.[0]?.attribues;
  }, [data]);
  return (
    <div
      className={twMerge(
        "relative flex max-h-dvh min-h-full w-full grow flex-col items-center justify-center overflow-hidden bg-slate-50 p-0 lg:flex-row",
        whiteLabel ? "" : "bg-white dark:bg-slate-950"
      )}
    >
      {!whiteLabel && (
        <>
          <div
            className={twMerge(
              overlayMode ? "w-full" : "max-h-96 w-full lg:max-h-full lg:w-1/2",
              "absolute top-0 right-auto bottom-0 left-0 z-0 h-full bg-black bg-center bg-cover transition-all",
              getAttribute !== AttributesHtmlElement.white && "bg-by-origin-app"
            )}
            style={{
              backgroundImage: "url(" + getImageURL + ")"
            }}
          />
          {origin !== FromURL.None && (
            <div
              className={twMerge(
                "absolute top-0 left-0 z-10 h-full lg:w-1/2",
                getAttribute !== AttributesHtmlElement.white && "bg-black opacity-25"
              )}
            />
          )}
          <div
            className={twMerge(
              "relative z-10 hidden max-h-96 max-h-dvh w-full grow flex-row items-center justify-center transition-all lg:flex lg:flex-col",
              !newUser && "max-h-0 lg:max-h-full"
            )}
          >
            <div className="w-full px-8 text-center lg:p-8 lg:text-left">
              <OlkyIntroduction data={data} mode={newUser ? "long" : "short"} />
            </div>
          </div>
        </>
      )}
      <div
        className={twMerge(
          "absolute right-auto bottom-20 left-0 z-0",
          overlayMode ? "w-full" : "w-full lg:w-1/2",
          "flex h-auto flex-row justify-center"
        )}
      >
        <div className="max-w-2xl px-6 xl:px-8">
          <Base className="text-sm text-white lg:text-base">{getFooter}</Base>
        </div>
      </div>
      <div
        className={twMerge(
          "z-10 flex grow justify-center overflow-auto",
          overlayMode ? "lg:h-auto" : "w-full grow bg-white dark:bg-slate-950"
        )}
      >
        <div className="absolute top-8 right-8 z-10 hidden lg:block">
          <LanguageSelector size="sm" />
        </div>
        <div
          className={twMerge(
            "flex w-full grow flex-col overflow-auto lg:flex-row lg:items-center",
            overlayMode ? "lg:max-w-xl" : ""
          )}
        >
          {/* OlkyPass image for mobile screens*/}
          <div
            className={`relative h-auto min-h-[150px] w-full shrink-0 bg-center bg-cover bg-no-repeat lg:hidden`}
            style={{
              backgroundImage: `url(${getImageURL})`
            }}
          >
            <div className="h-full w-full bg-black bg-opacity-40">
              <div>
                <div className="flex w-full items-center justify-between p-6">
                  {getLogo ? <Image src={getLogo} className="w-24" alt="Olky Logo" /> : <div></div>}
                  <LanguageSelector size="sm" />
                </div>
                {newUser && (
                  <div className="w-full px-8 text-center lg:p-8 lg:text-left">
                    <OlkyIntroduction data={data} mode={newUser ? "long" : "short"} />
                  </div>
                )}
              </div>
            </div>
          </div>
          <AnimatedForm
            className={twMerge(
              "mx-auto w-full bg-white p-6 lg:max-w-xl lg:rounded-lg dark:bg-slate-950",
              isRouteWithoutSpacing && "m-0 max-h-full w-full min-w-full p-0 lg:m-0 lg:p-0"
            )}
          >
            {children}
            <div className="mt-8" />
          </AnimatedForm>
        </div>
      </div>
    </div>
  );
};
